import {
  DateRangeFilter,
  PropertyDto,
  RangeFilterParams,
} from "@cma/shared/apis/comps/cma/generated";
import {
  INITIAL_FORM_FILTERS,
  FindListingsSchema,
  FIND_LISTINGS_FORM_MODEL_DEFAULT_VALUES,
} from "./property-search-filters-form-helpers";
import deepEqual from "deep-equal";
import {
  FindListingsAv8Request,
  NamedAreaFilterNameTypeEnum,
  FindListingsAv8RequestPropertyTypesEnum,
  OrderByParams,
} from "@shared/apis/sidekick/generated";
import { INITIAL_SORT_OPTION } from "../../../shared/helpers/property-search/comparable-sort-options";
import { FeaturedProperty } from "@cma/presentation-generation-logic";

export const numberConverter = (source?: string): number | undefined => {
  return source
    ? Number(source.replace(new RegExp(/[^0-9.-]/, "g"), ""))
    : undefined;
};

export const rangeConverter = (
  inputRange: (string | undefined)[],
): RangeFilterParams | undefined => {
  if (inputRange[0] || inputRange[1])
    return {
      startAt: numberConverter(inputRange[0]),
      endAt: numberConverter(inputRange[1]),
    };
  return undefined;
};

export const relativeDateConverter = (
  lastDays: string | undefined,
): DateRangeFilter | undefined => {
  if (!lastDays) return undefined;

  if (lastDays === "all-time")
    return {
      startAt: null,
      endAt: null,
    };

  const { Y: year = 0, M: month = 0, D: day = 0 } = JSON.parse(lastDays);

  const endDate = new Date();
  const startDate = new Date();

  // Add the negative values to go back in time
  startDate.setDate(startDate.getDate() + day);
  startDate.setMonth(startDate.getMonth() + month);
  startDate.setFullYear(startDate.getFullYear() + year);

  return {
    startAt: startDate,
    endAt: endDate,
  };
};

// const extractPolygonFilter = (mapFilter?: MapFilter) => {
//   if (mapFilter && mapFilter.shape === "polygon") {
//     return mapFilter.points.map(({ lat, lng: lon }) => ({ lat, lon }));
//   }
//   return undefined;
// };

// const extractRadiusFilter = (mapFilter?: MapFilter) => {
//   if (mapFilter && mapFilter.shape === "circle" && mapFilter.radius) {
//     const {
//       center: { lat, lng },
//       radius,
//     } = mapFilter;
//     return {
//       latitude: lat,
//       longitude: lng,
//       distance: (radius * 0.621371) / 1000,
//     };
//   }
//   return undefined;
// };

export const convertFiltersSchemeToSidekickFindListingsV3 = (
  input: Partial<FindListingsSchema>,
  subjectProperty?: Partial<PropertyDto>,
): FindListingsAv8Request => {
  const convertToNumberRange = (min?: string, max?: string) => {
    const startAt = min ? numberConverter(min) : undefined;
    const endAt = max ? numberConverter(max) : undefined;

    if (startAt !== undefined || endAt !== undefined) {
      return { startAt, endAt };
    }
    return undefined;
  };

  const getAreaFilter = (): FindListingsAv8Request["areaFilter"] => {
    // Priority order: most specific to least specific
    // Territory names may contain pipe-separated aliases (e.g. "Alamo Square|6e");
    // strip them before sending to the API.
    const stripAlias = (name: string) => name.split("|")[0].trim();

    if (input.streetAndNumber) {
      const context =
        input.city || input.state
          ? {
              city: input.city,
              stateAbbreviated: input.state,
            }
          : undefined;
      return {
        type: "NamedAreaFilter",
        name: input.streetAndNumber,
        nameType: NamedAreaFilterNameTypeEnum.StreetAndNumber,
        context,
      };
    }

    if (input.street) {
      const context =
        input.city || input.state
          ? {
              city: input.city,
              stateAbbreviated: input.state,
            }
          : undefined;
      return {
        type: "NamedAreaFilter",
        name: input.street,
        nameType: NamedAreaFilterNameTypeEnum.Street,
        context,
      };
    }

    if (input.zipCode) {
      const context =
        input.city || input.state
          ? {
              city: input.city,
              stateAbbreviated: input.state,
            }
          : undefined;
      return {
        type: "NamedAreaFilter",
        name: input.zipCode,
        nameType: NamedAreaFilterNameTypeEnum.ZipCode,
        context,
      };
    }

    if (input.neighborhood) {
      const context =
        input.city || input.state
          ? {
              city: input.city,
              stateAbbreviated: input.state,
            }
          : undefined;
      return {
        type: "NamedAreaFilter",
        name: stripAlias(input.neighborhood),
        nameType: NamedAreaFilterNameTypeEnum.Neighborhood,
        context,
      };
    }

    if (input.district) {
      const context =
        input.city || input.state
          ? {
              city: input.city,
              stateAbbreviated: input.state,
            }
          : undefined;
      return {
        type: "NamedAreaFilter",
        name: stripAlias(input.district),
        nameType: NamedAreaFilterNameTypeEnum.District,
        context,
      };
    }

    if (input.subDistrict) {
      const context =
        input.city || input.state
          ? {
              city: input.city,
              stateAbbreviated: input.state,
            }
          : undefined;
      return {
        type: "NamedAreaFilter",
        name: stripAlias(input.subDistrict),
        nameType: NamedAreaFilterNameTypeEnum.SubDistrict,
        context,
      };
    }

    if (input.schoolDistrict) {
      const context =
        input.city || input.state
          ? {
              city: input.city,
              stateAbbreviated: input.state,
            }
          : undefined;
      return {
        type: "NamedAreaFilter",
        name: stripAlias(input.schoolDistrict),
        nameType: NamedAreaFilterNameTypeEnum.SchoolDistrict,
        context,
      };
    }

    if (input.city) {
      const context = input.state
        ? {
            stateAbbreviated: input.state,
          }
        : undefined;

      return {
        type: "NamedAreaFilter",
        name: input.city,
        nameType: NamedAreaFilterNameTypeEnum.City,
        context,
      };
    }

    if (input.county) {
      const context = input.state
        ? {
            stateAbbreviated: input.state,
          }
        : undefined;

      return {
        type: "NamedAreaFilter",
        name: stripAlias(input.county),
        nameType: NamedAreaFilterNameTypeEnum.County,
        context,
      };
    }

    if (input.state) {
      return {
        type: "NamedAreaFilter",
        name: input.state,
        nameType: NamedAreaFilterNameTypeEnum.StateAbbreviated,
      };
    }

    return undefined;
  };

  const getAreaFilterFromMap = (): FindListingsAv8Request["areaFilter"] => {
    if (input.mapFilters?.shape === "circle") {
      return {
        type: "RadiusFilter",
        latitude: input.mapFilters.center.lat,
        longitude: input.mapFilters.center.lng,
        distance: input.mapFilters.radius / 1609.34, // Convert meters to miles
      };
    }
    if (input.mapFilters?.shape === "polygon") {
      return {
        type: "PolygonFilter",
        coordinates: input.mapFilters.points.map((point) => [
          point.lat,
          point.lng,
        ]),
      };
    }
    return undefined;
  };

  const getOrderResultsBy = (
    sortOption?: FindListingsSchema["sort"],
  ): OrderByParams | undefined => {
    if (!sortOption) return undefined;

    if (sortOption.criteria === "distance") {
      if (input.mapFilters?.shape === "circle" && input.mapFilters.center) {
        return {
          order: sortOption.asc ? "asc" : "desc",
          term: sortOption.criteria,
          latitude: input.mapFilters.center.lat,
          longitude: input.mapFilters.center.lng,
        };
      }

      if (subjectProperty?.latitude && subjectProperty?.longitude) {
        return {
          order: sortOption.asc ? "asc" : "desc",
          term: sortOption.criteria,
          latitude: subjectProperty.latitude,
          longitude: subjectProperty.longitude,
        };
      }

      return undefined;
    }

    return {
      order: sortOption.asc ? "asc" : "desc",
      term: sortOption.criteria as OrderByParams["term"],
      latitude: undefined as any,
      longitude: undefined as any,
    };
  };

  const convertedForm: FindListingsAv8Request = {
    operationName: "FindListingsV3",

    detailedResults: true,

    areaFilter: getAreaFilterFromMap() || getAreaFilter(),

    price: convertToNumberRange(input.priceMin, input.priceMax),

    propertyStatus: input.propertyStatus?.length
      ? (input.propertyStatus
          .filter((x) => Boolean(x))
          .map((x) => String(x)) as FindListingsAv8Request["propertyStatus"])
      : undefined,

    bedroomsCount: convertToNumberRange(input.bedsMin, input.bedsMax),

    bathroomsCount: convertToNumberRange(input.bathsMin, input.bathsMax),

    propertyTypes: input.propertyTypes?.length
      ? (input.propertyTypes
          .filter((x) => Boolean(x))
          .map((x) => String(x)) as FindListingsAv8Request["propertyTypes"])
      : undefined,

    // Living space
    livingSpaceSqFt: convertToNumberRange(
      input.livingSpaceMin,
      input.livingSpaceMax,
    ),

    lotSizeSqFt: convertToNumberRange(input.lotSizeMin, input.lotSizeMax),

    parkingGarageSpaces: convertToNumberRange(
      input.garageSpacesMin,
      input.garageSpacesMax,
    ),

    propertyYearBuilt: convertToNumberRange(
      input.yearBuiltMin,
      input.yearBuiltMax,
    ),

    listDate:
      input.listDateStart || input.listDateEnd
        ? {
            startAt: input.listDateStart || null,
            endAt: input.listDateEnd || null,
          }
        : undefined,

    soldDate:
      input.soldDateStart || input.soldDateEnd
        ? {
            startAt: input.soldDateStart || null,
            endAt: input.soldDateEnd || null,
          }
        : undefined,

    hasDoormanAttendance: input.doorman ? input.doorman === "yes" : undefined,

    waterfrontClassification: input.waterfrontClassification
      ? [input.waterfrontClassification]
      : undefined,

    // listingTransactionType: input.transactionType
    //   ? input.transactionType === "lease"
    //     ? "lease"
    //     : "sale"
    //   : undefined,

    paginationPage: input.page || 1,
    paginationLimit: input.limit || 20,

    orderResultsBy: getOrderResultsBy(input.sort),
  };

  const filteredValues = Object.fromEntries(
    Object.entries(convertedForm).filter(([_k, v]) => v !== undefined),
  ) as FindListingsAv8Request;

  return filteredValues;
};

export const convertWidgetFindListingsSearchToFiltersScheme = (
  input: FindListingsAv8Request,
): Partial<FindListingsSchema> => {
  const convertNumberRangeToStrings = (range?: {
    startAt?: number;
    endAt?: number;
  }) => {
    return {
      min: range?.startAt?.toString() || "",
      max: range?.endAt?.toString() || "",
    };
  };

  const getLocationFromAreaFilter = () => {
    const areaFilter = input.areaFilter;
    if (!areaFilter) return {};

    if (areaFilter.type === "NamedAreaFilter") {
      const { name, nameType, context } = areaFilter;

      // Handle all supported NamedAreaFilterNameTypeEnum values
      if (nameType === NamedAreaFilterNameTypeEnum.StreetAndNumber) {
        return {
          streetAndNumber: name,
          street: "",
          neighborhood: "",
          zipCode: "",
          city: context?.city || "",
          county: "",
          state: context?.stateAbbreviated || "",
          district: "",
          subDistrict: "",
          schoolDistrict: "",
        };
      }

      if (nameType === NamedAreaFilterNameTypeEnum.Street) {
        return {
          streetAndNumber: "",
          street: name,
          neighborhood: "",
          zipCode: "",
          city: context?.city || "",
          county: "",
          state: context?.stateAbbreviated || "",
          district: "",
          subDistrict: "",
          schoolDistrict: "",
        };
      }

      if (nameType === NamedAreaFilterNameTypeEnum.ZipCode) {
        return {
          streetAndNumber: "",
          street: "",
          neighborhood: "",
          zipCode: name,
          city: context?.city || "",
          county: "",
          state: context?.stateAbbreviated || "",
          district: "",
          subDistrict: "",
          schoolDistrict: "",
        };
      }

      if (nameType === NamedAreaFilterNameTypeEnum.Neighborhood) {
        return {
          streetAndNumber: "",
          street: "",
          neighborhood: name,
          zipCode: "",
          city: context?.city || "",
          county: "",
          state: context?.stateAbbreviated || "",
          district: "",
          subDistrict: "",
          schoolDistrict: "",
        };
      }

      if (nameType === NamedAreaFilterNameTypeEnum.District) {
        return {
          streetAndNumber: "",
          street: "",
          neighborhood: "",
          zipCode: "",
          city: context?.city || "",
          county: "",
          state: context?.stateAbbreviated || "",
          district: name,
          subDistrict: "",
          schoolDistrict: "",
        };
      }

      if (nameType === NamedAreaFilterNameTypeEnum.SubDistrict) {
        return {
          streetAndNumber: "",
          street: "",
          neighborhood: "",
          zipCode: "",
          city: context?.city || "",
          county: "",
          state: context?.stateAbbreviated || "",
          district: "",
          subDistrict: name,
          schoolDistrict: "",
        };
      }

      if (nameType === NamedAreaFilterNameTypeEnum.SchoolDistrict) {
        return {
          streetAndNumber: "",
          street: "",
          neighborhood: "",
          zipCode: "",
          city: context?.city || "",
          county: "",
          state: context?.stateAbbreviated || "",
          district: "",
          subDistrict: "",
          schoolDistrict: name,
        };
      }

      if (nameType === NamedAreaFilterNameTypeEnum.City) {
        const cityStateParts = name.split(", ");
        return {
          streetAndNumber: "",
          street: "",
          neighborhood: "",
          zipCode: "",
          city: cityStateParts[0] || name,
          county: "",
          state: cityStateParts[1] || context?.stateAbbreviated || "",
          district: "",
          subDistrict: "",
          schoolDistrict: "",
        };
      }

      if (nameType === NamedAreaFilterNameTypeEnum.County) {
        return {
          streetAndNumber: "",
          street: "",
          neighborhood: "",
          zipCode: "",
          city: "",
          county: name,
          state: context?.stateAbbreviated || "",
          district: "",
          subDistrict: "",
          schoolDistrict: "",
        };
      }

      if (nameType === NamedAreaFilterNameTypeEnum.StateAbbreviated) {
        return {
          streetAndNumber: "",
          street: "",
          neighborhood: "",
          zipCode: "",
          city: "",
          county: "",
          state: name,
          district: "",
          subDistrict: "",
          schoolDistrict: "",
        };
      }

      // Handle any other nameTypes gracefully (fallback to city-level)
      if (nameType) {
        return {
          streetAndNumber: "",
          street: "",
          neighborhood: "",
          zipCode: "",
          city: name,
          county: "",
          state: context?.stateAbbreviated || "",
          district: "",
          subDistrict: "",
          schoolDistrict: "",
        };
      }
    }

    return {
      streetAndNumber: "",
      street: "",
      neighborhood: "",
      zipCode: "",
      city: "",
      county: "",
      state: "",
      district: "",
      subDistrict: "",
      schoolDistrict: "",
    };
  };

  const getMapFiltersFromAreaFilter = (): FindListingsSchema["mapFilters"] => {
    const areaFilter = input.areaFilter;
    if (!areaFilter) return { shape: "none" };

    if (areaFilter.type === "RadiusFilter") {
      return {
        shape: "circle",
        center: {
          lat: areaFilter.latitude,
          lng: areaFilter.longitude,
        },
        radius: areaFilter.distance * 1609.34, // Convert miles to meters
      };
    }

    if (areaFilter.type === "PolygonFilter") {
      return {
        shape: "polygon",
        points: areaFilter.coordinates.map(([lat, lng]) => ({ lat, lng })),
      };
    }

    return { shape: "none" };
  };

  const priceRange = convertNumberRangeToStrings(input.price);
  const bedsRange = convertNumberRangeToStrings(input.bedroomsCount);
  const bathsRange = convertNumberRangeToStrings(input.bathroomsCount);
  const livingSpaceRange = convertNumberRangeToStrings(input.livingSpaceSqFt);
  const lotSizeRange = convertNumberRangeToStrings(input.lotSizeSqFt);
  const garageRange = convertNumberRangeToStrings(input.parkingGarageSpaces);
  const yearBuiltRange = convertNumberRangeToStrings(input.propertyYearBuilt);
  const locationData = getLocationFromAreaFilter();

  const converted: Partial<FindListingsSchema> = {
    propertyTypes: input.propertyTypes,
    propertyStatus: input.propertyStatus || [],

    priceMin: priceRange.min,
    priceMax: priceRange.max,

    bedsMin: bedsRange.min,
    bedsMax: bedsRange.max,
    bathsMin: bathsRange.min,
    bathsMax: bathsRange.max,

    livingSpaceMin: livingSpaceRange.min,
    livingSpaceMax: livingSpaceRange.max,
    lotSizeMin: lotSizeRange.min,
    lotSizeMax: lotSizeRange.max,

    garageSpacesMin: garageRange.min,
    garageSpacesMax: garageRange.max,
    yearBuiltMin: yearBuiltRange.min,
    yearBuiltMax: yearBuiltRange.max,

    neighborhood: locationData.neighborhood || "",
    city: locationData.city || "",
    state: locationData.state || "",
    streetAndNumber: locationData.streetAndNumber || "",
    street: locationData.street || "",
    zipCode: locationData.zipCode || "",
    county: locationData.county || "",
    district: locationData.district || "",
    subDistrict: locationData.subDistrict || "",
    schoolDistrict: locationData.schoolDistrict || "",

    listDateStart: input.listDate?.startAt || null,
    listDateEnd: input.listDate?.endAt || null,
    soldDateStart: input.soldDate?.startAt || null,
    soldDateEnd: input.soldDate?.endAt || null,

    doorman:
      input.hasDoormanAttendance === true
        ? "yes"
        : input.hasDoormanAttendance === false
          ? "no"
          : "",

    waterfrontClassification: input.waterfrontClassification?.[0],

    page: input.paginationPage || 1,
    limit: input.paginationLimit || 20,
    sort: input.orderResultsBy
      ? {
          criteria: input.orderResultsBy.term,
          asc: input.orderResultsBy.order === "asc",
        }
      : {
          criteria: INITIAL_SORT_OPTION.key,
          asc: INITIAL_SORT_OPTION.initialDirection === "asc",
        },

    mapFilters: getMapFiltersFromAreaFilter(),
  };

  // Filter out undefined values
  const filteredValues = Object.fromEntries(
    Object.entries(converted).filter(([_k, v]) => v !== undefined),
  ) as Partial<FindListingsSchema>;

  return filteredValues;
};

export const getInitialFindListingsInputFromCma = (
  property: FeaturedProperty | undefined | null,
): FindListingsAv8Request | null => {
  if (!property) {
    return null;
  }

  // Get the most relevant region (priority: neighborhood > city, zipcode)
  const getAreaFilter = (): FindListingsAv8Request["areaFilter"] => {
    if (property.latitude && property.longitude) {
      return {
        type: "RadiusFilter",
        latitude: property.latitude,
        longitude: property.longitude,
        distance: 1,
      };
    }

    if (property.neighborhood) {
      const context =
        property.city || property.state
          ? {
              city: property.city || undefined,
              stateAbbreviated: property.state || undefined,
            }
          : undefined;

      return {
        type: "NamedAreaFilter",
        name: property.neighborhood,
        nameType: NamedAreaFilterNameTypeEnum.Neighborhood,
        context,
      };
    }

    if (property.zipCode) {
      const context =
        property.city || property.state
          ? {
              city: property.city || undefined,
              stateAbbreviated: property.state || undefined,
            }
          : undefined;

      return {
        type: "NamedAreaFilter",
        name: property.zipCode,
        nameType: NamedAreaFilterNameTypeEnum.ZipCode,
        context,
      };
    }

    if (property.city) {
      const context = property.state
        ? {
            stateAbbreviated: property.state || undefined,
          }
        : undefined;

      return {
        type: "NamedAreaFilter",
        name: property.city,
        nameType: NamedAreaFilterNameTypeEnum.City,
        context,
      };
    }

    return undefined;
  };

  // Get bedrooms count (±1)
  const getBedroomsCount = (): FindListingsAv8Request["bedroomsCount"] => {
    if (property.beds && property.beds > 0) {
      return {
        startAt: Math.max(0, property.beds - 1),
        endAt: property.beds + 1,
      };
    }
    return undefined;
  };

  // Get bathrooms count (same +/- 1)
  const getBathroomsCount = (): FindListingsAv8Request["bathroomsCount"] => {
    if (property.baths && property.baths > 0) {
      return {
        startAt: property.baths - 1,
        endAt: property.baths + 1,
      };
    }
    return undefined;
  };

  // Get price range (current price +/- 20%)
  const getPriceRange = (): FindListingsAv8Request["price"] => {
    const isSold = property.listingStatus?.toLowerCase() === "sold";
    const currentPrice = isSold ? property.soldPrice : property.currentPrice;

    if (currentPrice && currentPrice > 0) {
      const minPrice = Math.max(0, currentPrice * 0.8);
      const maxPrice = currentPrice * 1.2;

      return {
        startAt: Math.round(minPrice),
        endAt: Math.round(maxPrice),
      };
    }
    return undefined;
  };

  // Get living space range (current space +/- 20%)
  const getLivingSpaceRange = (): FindListingsAv8Request["livingSpaceSqFt"] => {
    if (property.livingSpace && property.livingSpace > 0) {
      const minSpace = Math.max(0, property.livingSpace * 0.8);
      const maxSpace = property.livingSpace * 1.2;

      return {
        startAt: Math.round(minSpace),
        endAt: Math.round(maxSpace),
      };
    }
    return undefined;
  };

  // Get year built range (current year +/- 10 years)
  const getYearBuiltRange = (): FindListingsAv8Request["propertyYearBuilt"] => {
    if (property.yearBuilt && property.yearBuilt > 0) {
      return {
        startAt: property.yearBuilt - 10,
        endAt: property.yearBuilt + 10,
      };
    }
    return undefined;
  };

  // Get sold date range (6 months before now)
  const getSoldDateRange = (): FindListingsAv8Request["soldDate"] => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 6);

    return {
      startAt: startDate,
      endAt: endDate,
    };
  };

  const getPropertyTypes = (): FindListingsAv8Request["propertyTypes"] => {
    if (property.isLease) {
      return [FindListingsAv8RequestPropertyTypesEnum.Lease];
    }
    return [FindListingsAv8RequestPropertyTypesEnum.Sale];
  };

  return {
    operationName: "FindListingsV3",
    detailedResults: true,
    areaFilter: getAreaFilter(),
    bedroomsCount: getBedroomsCount(),
    bathroomsCount: getBathroomsCount(),
    price: getPriceRange(),
    propertyTypes: getPropertyTypes(),
    propertyStatus: ["sold"],
    livingSpaceSqFt: getLivingSpaceRange(),
    lotSizeSqFt: undefined,
    parkingGarageSpaces: undefined,
    propertyYearBuilt: getYearBuiltRange(),
    soldDate: getSoldDateRange(),
    listDate: undefined,
  };
};

const hasValue = (base: unknown, value: unknown) => {
  if (Array.isArray(value)) {
    return (value as any[]).filter((x) => Boolean(x)).length > 0;
  }
  return !deepEqual(base, value);
};

export const isModelEnoughToSearch = (model: FindListingsSchema): boolean => {
  if (!model.dealType) {
    return false;
  }

  const searchEnablers: Array<keyof FindListingsSchema> = [
    ...(Object.keys(INITIAL_FORM_FILTERS) as Array<
      keyof typeof INITIAL_FORM_FILTERS
    >),
    "mapFilters",
  ];

  return Boolean(
    searchEnablers.find((key) => {
      return hasValue(FIND_LISTINGS_FORM_MODEL_DEFAULT_VALUES[key], model[key]);
    }),
  );
};

export const activeFiltersCount = (model: FindListingsSchema): number => {
  const filtersAvailable: Array<keyof FindListingsSchema> = [
    ...(Object.keys(INITIAL_FORM_FILTERS) as Array<
      keyof typeof INITIAL_FORM_FILTERS
    >),
    "mapFilters",
  ];
  const fieldsToIgnore = ["transactionType", "radius"];

  const changedValues = filtersAvailable
    .filter((x) => !fieldsToIgnore.includes(x))
    .filter((key) => {
      return hasValue(FIND_LISTINGS_FORM_MODEL_DEFAULT_VALUES[key], model[key]);
    });
  return changedValues.length;
};

export const normalizeModel = (model: FindListingsSchema) => {
  const filteredEntries = Object.entries(model).map(([k, v]) => {
    if (v === null || v === undefined || v === "") return [];
    if (Array.isArray(v)) {
      const onlyChecked = v.filter((x) => Boolean(x));
      if (!onlyChecked.length) {
        return [];
      }
      return [k, onlyChecked];
    }
    if (
      k === "mapFilters" &&
      (v as FindListingsSchema["mapFilters"])?.shape === "none"
    )
      return [];
    return [k, v];
  });

  return Object.fromEntries(filteredEntries);
};
